import { LoadingSpinner } from '@neo4j-ndl/react';
import { Neo4jGraphQL } from '@neo4j/graphql';
import type { AnalyticsTrackPayload } from '@nx/analytics-service';
import { DATA_API_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import type { DataApiBatchResult, Instance } from '@nx/state';
import { INSTANCE_STATUS, consoleApi, graphqlApi, useActiveProject, useTrackEvent } from '@nx/state';
import { Center } from '@nx/ui';
import { useCallback, useEffect, useState } from 'react';

import { ApiErrorBanner } from '../components/api-error-banner';
import { EmptyState } from '../components/empty-state';
import { NoInstancesState } from '../components/no-instance-state';
import { withUpxContext } from '../components/upx-context';
import { ALLOWED_INSTANCE_TIERS, POLLING_INTERVAL_DATA_APIS } from '../settings';
import { MODIFY_MODE } from '../types';
import { CreateDataApi } from './create';
import { EditDataApi } from './edit';
import { Overview } from './overview';

const ManagementMain = () => {
  if (navigator.webdriver) {
    const ctorStr = Neo4jGraphQL.toString();

    if (ctorStr.includes('complexityEstimatorHelper')) {
      // eslint-disable-next-line no-console
      console.log('Using 7.x version of @neo4j/graphql in data api');
    } else {
      // eslint-disable-next-line no-console
      console.log('Using 5.x version of @neo4j/graphql in data api');
    }
  }

  const track = useTrackEvent<AnalyticsTrackPayload & { event: DATA_API_EVENTS }>();
  const activeProject = useActiveProject();
  const [getInstance, { error: errorInstance }] = consoleApi.useLazyGetInstanceQuery();
  const [getDataApiBatch, { data: dataApis, error: errorDataApis }] = graphqlApi.useLazyGetDataApisBatchQuery();
  const { data: instanceSummaries = [], error: errorInstanceSummaries } = consoleApi.useListInstancesQuery(
    activeProject.id,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const [runningInstances, setRunningInstances] = useState<Instance[]>([]);
  const [hasCheckedInstancesInitially, setHasCheckedInstancesInitially] = useState(false);

  const [modifyMode, setModifyMode] = useState<MODIFY_MODE>(MODIFY_MODE.closed);
  const [activeModifyDataApi, setActiveModifyDataApi] = useState<DataApiBatchResult | null>(null);

  const runningInstancesWithoutDataApi = runningInstances.filter(
    (instance) => !(dataApis?.data.some((api) => api.instanceId === instance.id) ?? false),
  );

  useEffect(() => {
    async function fetchDetailsOfRunningInstances() {
      if (instanceSummaries.length === 0) {
        setRunningInstances([]);
        setHasCheckedInstancesInitially(true);
        return;
      }
      if (instanceSummaries.length > 0) {
        const promises = instanceSummaries.map(({ id }) => getInstance(id).unwrap());
        const detailedInstances = await Promise.all(promises);

        // Filter out instances that are not running or are not of an allowed tier
        const theRunningInstances = detailedInstances.filter(
          (instance): instance is Instance =>
            instance.desiredSettings.version === '5' &&
            instance.instanceStatus === INSTANCE_STATUS.RUNNING &&
            ALLOWED_INSTANCE_TIERS.includes(instance.tier),
        );
        setRunningInstances(theRunningInstances);
        setHasCheckedInstancesInitially(true);
      }
    }
    void fetchDetailsOfRunningInstances();
  }, [getInstance, instanceSummaries]);

  const fetchDataApis = useCallback(async () => {
    if (!hasCheckedInstancesInitially) {
      return;
    }

    if (runningInstances.length > 0) {
      const args = runningInstances.map((instance) => ({
        projectId: activeProject.id,
        instanceId: instance.id,
        instanceName: instance.name,
      }));

      await getDataApiBatch(args).unwrap();
    }
  }, [activeProject.id, runningInstances, hasCheckedInstancesInitially, getDataApiBatch]);

  useEffect(() => {
    void fetchDataApis();
    const intervalId = setInterval(() => {
      if (modifyMode === MODIFY_MODE.closed) {
        void fetchDataApis();
      }
    }, POLLING_INTERVAL_DATA_APIS);
    return () => clearInterval(intervalId);
  }, [fetchDataApis, modifyMode]);

  if (!hasCheckedInstancesInitially) {
    return (
      <Center>
        <LoadingSpinner size="large" />
      </Center>
    );
  }

  if (runningInstances.length === 0) {
    return <NoInstancesState />;
  }

  if (dataApis) {
    if (modifyMode === MODIFY_MODE.closed) {
      if (dataApis.data.length === 0) {
        return (
          <EmptyState
            onClickCreateApi={() => {
              setModifyMode(MODIFY_MODE.create);
              track({
                event: DATA_API_EVENTS.NAV_TO_CREATE,
                scope: APP_SCOPE.dataApi,
                properties: {
                  origin: 'empty-state',
                },
              });
            }}
          />
        );
      }

      if (dataApis.data.length > 0) {
        return (
          <Overview
            dataApis={dataApis}
            fetchDataApis={fetchDataApis}
            setActiveModifyDataApi={setActiveModifyDataApi}
            setModifyMode={setModifyMode}
          />
        );
      }
    }

    return (
      <div className="h-full overflow-auto p-4" data-testid="data-api-app">
        {errorInstanceSummaries && <ApiErrorBanner error={errorInstanceSummaries} hasIcon className="m-8" />}
        {errorDataApis && <ApiErrorBanner error={errorDataApis} hasIcon className="m-8" />}
        {errorInstance && <ApiErrorBanner error={errorInstance} hasIcon className="m-8" />}
        {modifyMode === MODIFY_MODE.create ? (
          <CreateDataApi
            runningInstancesWithoutDataApi={runningInstancesWithoutDataApi}
            closeModifyPage={() => {
              setActiveModifyDataApi(null);
              setModifyMode(MODIFY_MODE.closed);
            }}
          />
        ) : (
          <EditDataApi
            runningInstances={runningInstances}
            closeModifyPage={() => {
              setActiveModifyDataApi(null);
              setModifyMode(MODIFY_MODE.closed);
            }}
            activeModifyDataApi={activeModifyDataApi}
          />
        )}
      </div>
    );
  }

  // This is a fallback where there is a slight delay between the dataApis being fetched and the UI being updated
  return (
    <Center>
      <LoadingSpinner size="large" />
    </Center>
  );
};
const ManagementMainWithUpxContext = withUpxContext(ManagementMain);

export default ManagementMainWithUpxContext;
